import { useEffect, useMemo, useState } from "react";
import { ExternalLink, FileText, ImageOff } from "lucide-react";
import type { ImmotionAttachment } from "@myapp/contract";
import {
  attachmentByteSize,
  createAttachmentUrl,
  formatBytes,
} from "../../../../shared/lib/formatBytes";

type Props = {
  attachments: ImmotionAttachment[] | null | undefined;
  title?: string;
  className?: string;
};

type Entry = {
  attachment: ImmotionAttachment;
  url: string | null;
  key: string;
};

export const ImmotionAttachmentsGallery = ({
  attachments,
  title = "Immotion-Anhänge",
  className,
}: Props) => {
  const items = useMemo(() => attachments ?? [], [attachments]);

  // Object URLs instead of inline `data:` URIs — they navigate in a new tab and
  // keep megabytes of base64 out of the DOM.
  const entries = useMemo<Entry[]>(
    () =>
      items.map((attachment, i) => ({
        attachment,
        url: createAttachmentUrl(attachment),
        key: `${attachment.filename}-${i}`,
      })),
    [items],
  );

  useEffect(
    () => () => {
      entries.forEach(({ url }) => url && URL.revokeObjectURL(url));
    },
    [entries],
  );

  const [broken, setBroken] = useState<Record<string, boolean>>({});

  useEffect(() => setBroken({}), [entries]);

  const images = entries.filter((e) => e.attachment.mime.startsWith("image/"));
  const others = entries.filter((e) => !e.attachment.mime.startsWith("image/"));

  const totalSize = useMemo(
    () => items.reduce((sum, a) => sum + attachmentByteSize(a), 0),
    [items],
  );

  if (items.length === 0) return null;

  return (
    <div className={className}>
      <div className="flex items-baseline justify-between gap-2 mb-2">
        <div className="text-[13px] text-[#555]">
          {title} ({items.length})
        </div>
        <div className="text-[11px] text-[#8a8a8a]">{formatBytes(totalSize)}</div>
      </div>

      {images.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
          {images.map(({ attachment: a, url, key }) => {
            const failed = !url || broken[key];

            if (failed) {
              return (
                <div
                  key={key}
                  title={a.filename}
                  // `min-w-0`: same latent "grid blowout" as the fallback tile below — a grid
                  // item's default `min-width: auto` lets an `aspect-square` box fall back to its
                  // intrinsic size instead of the grid track when there's no sibling column
                  // forcing it down (visible with exactly one image).
                  className="flex aspect-square w-full min-w-0 flex-col items-center justify-center gap-1 overflow-hidden rounded-md border border-slate-200 bg-slate-100 text-slate-400"
                >
                  <ImageOff size={20} />
                  <span className="max-w-full truncate px-2 text-[11px]">
                    {a.filename}
                  </span>
                </div>
              );
            }

            return (
              <a
                key={key}
                href={url}
                target="_blank"
                rel="noreferrer"
                title={`${a.filename} öffnen`}
                aria-label={`${a.filename} öffnen`}
                className="group relative block aspect-square w-full min-w-0 overflow-hidden rounded-md border border-slate-200 bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#124093]"
              >
                <img
                  src={url}
                  alt={a.filename}
                  onError={() => setBroken((prev) => ({ ...prev, [key]: true }))}
                  className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-[1.03]"
                  loading="lazy"
                />

                <span className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center gap-1 bg-gradient-to-t from-black/70 to-transparent px-2 pt-6 pb-1 text-left text-[11px] text-white opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
                  <ExternalLink size={12} className="shrink-0" />
                  <span className="truncate">{a.filename}</span>
                </span>
              </a>
            );
          })}
        </div>
      )}

      {others.length > 0 && (
        <ul className="mt-2 flex flex-col gap-1">
          {others.map(({ attachment: a, url, key }) => (
            <li key={key}>
              <a
                href={url ?? undefined}
                target="_blank"
                rel="noreferrer"
                title={`${a.filename} öffnen`}
                className="flex w-full items-center gap-2 rounded-md border border-slate-200 px-2 py-1.5 text-left text-sm text-[#124093] hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#124093] aria-disabled:pointer-events-none aria-disabled:text-slate-400"
                aria-disabled={url ? undefined : true}
              >
                <FileText size={14} className="shrink-0 text-slate-400" />
                <span className="truncate">{a.filename}</span>
                <span className="ml-auto shrink-0 text-[11px] text-[#8a8a8a]">
                  {formatBytes(attachmentByteSize(a))}
                </span>
                <ExternalLink size={14} className="shrink-0" />
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
